import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface ExecutionView {
  readonly testExecutionId: string;
  readonly executionCode: string;
  readonly testCaseId: string;
  readonly testPlanId: string | null;
  readonly executionCycle: string | null;
  readonly executionDate: string | null;
  readonly environment: string | null;
  readonly result: string | null;
  readonly actualResults: string | null;
  readonly comments: string | null;
  readonly defectsRaised: number | null;
  readonly durationSeconds: number | null;
  readonly status: string;
}

const RECORD_RESULTS = ['PASS', 'FAIL', 'BLOCKED', 'SKIPPED'];

/**
 * GR_M31-M35 §L34.03 / §M — test execution (run) detail viewer
 * (`/admin/qms/executions/:id`). Renders a single test execution (T451) with its
 * parent suite / plan (§M L34.03 run.suite_id → test_plan_id), the recorded
 * result and the count of defects raised (§M run.result.pass_count is derived
 * from the pass/fail result plus the linked defects), and drives the run
 * lifecycle — record a result, approve and cancel — each wired to the guarded
 * /v1/qms/test-executions write API, so a QA lead can action a single run without
 * leaving the detail view.
 */
export function ExecutionDetailPage(): ReactNode {
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [exec, setExec] = useState<ExecutionView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [recordResult, setRecordResult] = useState('PASS');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    if (!id) { setError(t('m34.execDetail.noId')); setLoading(false); return; }
    setLoading(true);
    apiRequest<{ data: ExecutionView }>(`/v1/qms/test-executions/${id}`, { token })
      .then((r) => { if (alive) { setExec(r.data); setError(null); } })
      .catch((e) => { if (alive) setError(e instanceof ApiError ? e.message : t('m34.execDetail.loadFailed')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, id, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m34.common.notAuthorised', { perm })
      : (err as Error).message;

  const act = async (path: string, label: string, perm: string, body?: Record<string, unknown>): Promise<void> => {
    if (!id) return;
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/qms/test-executions/${id}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(t('m34.execDetail.notice', { label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, perm));
    }
  };

  if (loading) return <main aria-label="execution-detail"><p>{t('m34.common.loading')}</p></main>;
  if (error) return <main aria-label="execution-detail"><p role="alert">{error}</p></main>;
  if (!exec) return <main aria-label="execution-detail"><p role="alert">{t('m34.execDetail.notFound')}</p></main>;

  const resultLabel = exec.result ?? 'NOT_RUN';

  return (
    <main aria-label="execution-detail">
      <h1>{exec.executionCode}</h1>
      <dl>
        <dt>{t('m34.execDetail.dtTestCase')}</dt><dd aria-label="execution-test-case">{exec.testCaseId}</dd>
        <dt>{t('m34.execDetail.dtSuite')}</dt><dd aria-label="execution-suite">{exec.testPlanId ?? '—'}</dd>
        <dt>{t('m34.execDetail.dtCycle')}</dt><dd aria-label="execution-cycle">{exec.executionCycle ?? '—'}</dd>
        <dt>{t('m34.execDetail.dtEnvironment')}</dt><dd aria-label="execution-environment">{exec.environment ?? '—'}</dd>
        <dt>{t('m34.execDetail.dtResult')}</dt><dd aria-label="execution-result" data-result={resultLabel}>{resultLabel}</dd>
        <dt>{t('m34.execDetail.dtDefects')}</dt><dd aria-label="execution-defects">{exec.defectsRaised ?? 0}</dd>
        <dt>{t('m34.execDetail.dtExecutedAt')}</dt><dd aria-label="execution-date">{day(exec.executionDate)}</dd>
        <dt>{t('m34.common.status')}</dt><dd aria-label="execution-status">{exec.status}</dd>
      </dl>

      <section aria-label="execution-lifecycle">
        <h2>{t('m34.execDetail.lifecycle')}</h2>
        {(exec.status === 'NOT_STARTED' || exec.status === 'IN_PROGRESS') && (
          <>
            <select aria-label="record-result" value={recordResult} onChange={(e) => setRecordResult(e.target.value)}>
              {RECORD_RESULTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <Button variant="primary" type="button" aria-label="record-execution" onClick={() => void act('record', t('m34.execDetail.act.recorded'), 'M34_EXECUTION_MANAGE', { result: recordResult })}>{t('m34.execDetail.btnRecord')}</Button>
            <Button variant="danger" type="button" aria-label="cancel-execution" onClick={() => void act('cancel', t('m34.execDetail.act.cancelled'), 'M34_EXECUTION_MANAGE')}>{t('m34.execDetail.btnCancel')}</Button>
          </>
        )}
        {exec.status === 'COMPLETED' && (
          <Button variant="secondary" type="button" aria-label="approve-execution" onClick={() => void act('approve', t('m34.execDetail.act.approved'), 'M34_EXECUTION_APPROVE')}>{t('m34.execDetail.btnApprove')}</Button>
        )}
        {actionError && <p role="alert" aria-label="execution-action-error">{actionError}</p>}
        {notice && <p aria-label="execution-action-notice">{notice}</p>}
      </section>

      {exec.actualResults && (
        <section aria-label="execution-actual-results">
          <h2>{t('m34.execDetail.actualResults')}</h2>
          <p>{exec.actualResults}</p>
        </section>
      )}

      {exec.comments && <p aria-label="execution-comments">{exec.comments}</p>}
    </main>
  );
}
